import React from 'react';
import { MoreVertical } from 'lucide-react';
import { Button } from "@/modern/components/ui/button-shadcn";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { useTimelineContext } from '../context/TimelineContext';
import { FilterKey, TimelineMode } from '../utils/types';
import { VIEW_TYPES } from '@/other/constants/OtherKeys';
import { SWIVU_STANDARD_ICONS } from '@/other/constants/IconKeys';

export const MODE_CONFIG: Record<
  FilterKey,
  {
    label: string;
    icon: string;
  }
> = {
  status: {
    label: "Status Changes",
    icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.tasks]
  },
  schedule: {
    label: "Scheduled Resources",
    icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.schedulerView]
  },
  planning: {
    label: "Planning Events",
    icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.planView]
  },
  reminders: {
    label: "Reminders",
    icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.reminder]
  },
  history: {
    label: "History",
    icon: "fa-light fa-clock-rotate-left"
  }
};

export const TimelineHeader: React.FC = () => {
  const {
    config,
    selectedMode,
    setSelectedMode,
    handleCreateReminder,
    handleCreateSchedule,
    handleCreatePlanning
  } = useTimelineContext();

  const getContextMenuItems = () => {
    const items: React.ReactNode[] = [];

    if (selectedMode === 'reminders' && config.createActions.includes('reminder')) {
      items.push(
        <DropdownMenuItem key="add-reminder" onClick={() => handleCreateReminder()}>
          <i className={`${MODE_CONFIG.reminders.icon} h-4 w-4 mr-2`} />
          Add Reminder
        </DropdownMenuItem>
      );
    }

    if (selectedMode === 'schedule' && config.createActions.includes('schedule')) {
      items.push(
        <DropdownMenuItem key="add-schedule" onClick={handleCreateSchedule}>
          <i className={`${MODE_CONFIG.schedule.icon} h-4 w-4 mr-2`} />
          Add Schedule Date
        </DropdownMenuItem>
      );
    }

    if (selectedMode === 'planning' && config.createActions.includes('planning')) {
      items.push(
        <DropdownMenuItem key="add-planning" onClick={() => handleCreatePlanning()}>
          <i className={`${MODE_CONFIG.planning.icon} h-4 w-4 mr-2`} />
          Add Planning Date
        </DropdownMenuItem>
      );
    }

    return items;
  };

  const contextMenuItems = getContextMenuItems();
  const hasContextMenu = contextMenuItems.length > 0;

  return (
    <div className="flex-none py-0 bg-card/50 dark:bg-card px-4 h-[3.25rem] border-b flex flex-row items-center justify-between gap-2 space-y-0">
      <div className="text-base tracking-tight text-foreground truncate min-w-0">Timeline</div>

      <ButtonGroup className="shrink-0">
        <Select
          value={selectedMode || ""}
          onValueChange={(value) => setSelectedMode(value as TimelineMode)}
        >
          <SelectTrigger className="!h-9 w-auto gap-2 bg-background dark:bg-background border shadow-xs hover:bg-accent hover:text-foreground dark:border-input">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {config.availableFilters.map((filter) => {
              const modeConfig = MODE_CONFIG[filter];
              return (
                <SelectItem key={filter} value={filter} textValue={modeConfig.label}>
                  <div className="flex items-center gap-2">
                    <i className={`${modeConfig.icon} text-xs shrink-0`} />
                    <span className="text-sm truncate">{modeConfig.label}</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {hasContextMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="entityHeader">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Timeline actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {contextMenuItems}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </ButtonGroup>
    </div>
  );
};
